import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import styled from 'styled-components';
import api from '../api/axiosConfig';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

Modal.setAppElement('#root');

/* ================== UI ================== */

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(255, 255, 255, 0.94);
  margin-top: 2rem;
  border-radius: 18px 18px 0 0;
`;

const Title = styled.div`
  font-weight: 950;
  color: #053969;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CloseBtn = styled.button`
  border: 1px solid rgba(0, 0, 0, 0.2);
  background: transparent;
  border-radius: 999px;
  padding: 6px 10px;
  cursor: pointer;
  font-weight: 900;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

const Body = styled.div`
  padding: 14px;
  background: rgba(255, 255, 255, 0.94);
`;

const Tabs = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 12px;
`;

const Tab = styled.button`
  border: 1px solid rgba(5, 57, 105, 0.22);
  background: ${(p) => (p.$active ? '#053969' : 'transparent')};
  color: ${(p) => (p.$active ? '#fff' : '#053969')};
  border-radius: 999px;
  padding: 10px 12px;
  font-weight: 950;
  cursor: pointer;
  transition: 0.15s ease;
  font-size: 13px;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.98;
  }

  &:active {
    transform: translateY(0px);
  }
`;

const Form = styled.form`
  display: grid;
  gap: 12px;
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.label`
  display: grid;
  gap: 6px;
`;

const Label = styled.span`
  font-size: 13px;
  font-weight: 900;
  color: rgba(0, 0, 0, 0.75);
`;

const Input = styled.input`
  border: 1px solid rgba(5, 57, 105, 0.18);
  background: rgba(255, 255, 255, 0.75);
  border-radius: 14px;
  padding: 12px 12px;
  font-size: 14px;
  outline: none;

  &:focus {
    border-color: rgba(5, 57, 105, 0.45);
    box-shadow: 0 0 0 4px rgba(5, 57, 105, 0.12);
  }
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
`;

const Check = styled.label`
  display: inline-flex;
  gap: 8px;
  align-items: flex-start;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.7);
  line-height: 1.45;
`;

const PrimaryBtn = styled.button`
  border: none;
  cursor: pointer;
  border-radius: 999px;
  padding: 12px 14px;
  font-weight: 950;
  background: #b7ff53;
  color: #0b0b0b;

  &:hover {
    transform: translateY(-1px);
  }
  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Muted = styled.div`
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  line-height: 1.45;
`;

const ErrorBox = styled.div`
  border: 1px solid rgba(220, 38, 38, 0.25);
  background: rgba(220, 38, 38, 0.08);
  color: rgba(0, 0, 0, 0.75);
  border-radius: 14px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
`;

function isEmail(v) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).toLowerCase());
}

/**
 * Защита от "/api/api/...":
 * - если где-то по ошибке передали полный путь "/api/..."
 *   мы нормализуем его до "/..."
 * - если путь без "/api" — оставляем как есть
 */
function safeApiPath(path) {
  const p = String(path || '');
  if (!p) return p;

  // Уберём случайные двойные слэши (кроме протокола, но тут относительные пути)
  const normalized = p.replace(/\/{2,}/g, '/');

  // Если начинается с "/api/" или "api/" — вырежем префикс "api"
  if (normalized.startsWith('/api/')) return normalized.replace(/^\/api/, '');
  if (normalized.startsWith('api/')) return normalized.replace(/^api/, '');

  // Если ровно "/api" или "api" — превратим в "/"
  if (normalized === '/api') return '/';
  if (normalized === 'api') return '/';

  return normalized;
}

export default function AuthModal({ open, mode = 'signin', onClose, onAuthSuccess }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [tab, setTab] = useState(mode);

  useEffect(() => {
    if (open) setTab(mode);
  }, [open, mode]);

  // Sign in
  const [inEmail, setInEmail] = useState('');
  const [inPass, setInPass] = useState('');
  const [remember, setRemember] = useState(true);

  // Sign up
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [upEmail, setUpEmail] = useState('');
  const [upPass, setUpPass] = useState('');
  const [upPass2, setUpPass2] = useState('');
  const [agree, setAgree] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const title = useMemo(
    () => (tab === 'signin' ? t('auth.titleSignIn') : t('auth.titleSignUp')),
    [tab, t],
  );

  const resetErrors = () => setError('');

  const afterAuth = async () => {
    const me = await api.get(safeApiPath('/me/'));
    onAuthSuccess?.(me.data);

    onClose?.();
    navigate('/me');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    resetErrors();

    if (!isEmail(inEmail)) return setError(t('auth.errors.emailInvalid'));
    if (inPass.trim().length < 6) return setError(t('auth.errors.passwordTooShort'));

    try {
      setLoading(true);

      const res = await api.post(safeApiPath('/auth/signin/'), {
        username: inEmail,
        password: inPass,
      });

      localStorage.setItem('access', res.data.access);
      localStorage.setItem('refresh', res.data.refresh);
      localStorage.setItem('remember', remember ? '1' : '0');

      await afterAuth();
    } catch (e2) {
      setError(t('auth.errors.loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    resetErrors();

    if (first.trim().length < 2) return setError(t('auth.errors.firstNameTooShort'));
    if (last.trim().length < 2) return setError(t('auth.errors.lastNameTooShort'));
    if (!isEmail(upEmail)) return setError(t('auth.errors.emailInvalid'));
    if (upPass.trim().length < 6) return setError(t('auth.errors.passwordTooShort'));
    if (upPass !== upPass2) return setError(t('auth.errors.passwordsNoMatch'));
    if (!agree) return setError(t('auth.errors.mustAcceptTermsPrivacy'));

    setLoading(true);

    try {
      const res = await api.post(safeApiPath('/auth/signup/'), {
        email: upEmail,
        username: upEmail,
        first_name: first,
        last_name: last,
        password: upPass,
      });

      localStorage.setItem('access', res.data.tokens.access);
      localStorage.setItem('refresh', res.data.tokens.refresh);
    } catch (e2) {
      const data = e2?.response?.data || {};
      const message =
        data?.email?.[0] ||
        data?.username?.[0] ||
        data?.password?.[0] ||
        data?.detail ||
        t('auth.errors.signupFailed');

      setError(message);
      setLoading(false);
      return;
    }

    try {
      await afterAuth();
    } catch (e2) {
      console.error('Post-signup authentication failed:', e2);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onRequestClose={onClose}
      contentLabel={t('auth.modalLabel')}
      style={{
        overlay: { backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 10000 },
        content: {
          maxWidth: '640px',
          width: 'calc(100% - 24px)',
          margin: 'auto',
          borderRadius: '18px',
          padding: '0',
          border: 'none',
          inset: '50% auto auto 50%',
          transform: 'translate(-50%, -50%)',
          overflow: 'hidden',
          boxShadow: '0 18px 60px rgba(0,0,0,0.45)',
          background: 'transparent',
        },
      }}
    >
      <Head>
        <Title>{title}</Title>
        <CloseBtn type="button" onClick={onClose}>
          ✕
        </CloseBtn>
      </Head>

      <Body>
        <Tabs>
          <Tab
            type="button"
            $active={tab === 'signin'}
            onClick={() => {
              setTab('signin');
              resetErrors();
            }}
          >
            {t('auth.tabSignIn')}
          </Tab>
          <Tab
            type="button"
            $active={tab === 'signup'}
            onClick={() => {
              setTab('signup');
              resetErrors();
            }}
          >
            {t('auth.tabSignUp')}
          </Tab>
        </Tabs>

        {error ? <ErrorBox>{error}</ErrorBox> : null}

        {tab === 'signin' ? (
          <Form onSubmit={handleSignIn}>
            <Field>
              <Label>{t('auth.email')}</Label>
              <Input
                type="email"
                autoComplete="email"
                placeholder={t('auth.placeholders.email')}
                value={inEmail}
                onChange={(e) => setInEmail(e.target.value)}
              />
            </Field>

            <Field>
              <Label>{t('auth.password')}</Label>
              <Input
                type="password"
                autoComplete="current-password"
                placeholder={t('auth.placeholders.password')}
                value={inPass}
                onChange={(e) => setInPass(e.target.value)}
              />
            </Field>

            <Row>
              <Check>
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                />
                {t('auth.rememberMe')}
              </Check>

              <Muted>
                {t('auth.forgotPassword')} <span style={{ opacity: 0.7 }}>{t('auth.soon')}</span>
              </Muted>
            </Row>

            <PrimaryBtn type="submit" disabled={loading}>
              {loading ? t('auth.btnSigningIn') : t('auth.btnSignIn')}
            </PrimaryBtn>
          </Form>
        ) : (
          <Form onSubmit={handleSignUp}>
            <Grid2>
              <Field>
                <Label>{t('auth.firstName')}</Label>
                <Input
                  type="text"
                  autoComplete="given-name"
                  placeholder={t('auth.placeholders.firstName')}
                  value={first}
                  onChange={(e) => setFirst(e.target.value)}
                />
              </Field>

              <Field>
                <Label>{t('auth.lastName')}</Label>
                <Input
                  type="text"
                  autoComplete="family-name"
                  placeholder={t('auth.placeholders.lastName')}
                  value={last}
                  onChange={(e) => setLast(e.target.value)}
                />
              </Field>
            </Grid2>

            <Field>
              <Label>{t('auth.email')}</Label>
              <Input
                type="email"
                autoComplete="email"
                placeholder={t('auth.placeholders.email')}
                value={upEmail}
                onChange={(e) => setUpEmail(e.target.value)}
              />
            </Field>

            <Grid2>
              <Field>
                <Label>{t('auth.password')}</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('auth.placeholders.password')}
                  value={upPass}
                  onChange={(e) => setUpPass(e.target.value)}
                />
              </Field>

              <Field>
                <Label>{t('auth.confirmPassword')}</Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  placeholder={t('auth.placeholders.confirmPassword')}
                  value={upPass2}
                  onChange={(e) => setUpPass2(e.target.value)}
                />
              </Field>
            </Grid2>

            <Check>
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                {t('auth.agreePrefix')} <span style={{ fontWeight: 950 }}>{t('auth.terms')}</span>{' '}
                {t('auth.and')} <span style={{ fontWeight: 950 }}>{t('auth.privacyPolicy')}</span>
                {t('auth.agreeSuffix')}
              </span>
            </Check>

            <PrimaryBtn type="submit" disabled={loading}>
              {loading ? t('auth.btnCreating') : t('auth.btnCreateAccount')}
            </PrimaryBtn>
          </Form>
        )}
      </Body>
    </Modal>
  );
}
